/* eslint-disable @typescript-eslint/no-empty-function */

import { useState } from 'react'
import ResizeObserver from 'resize-observer-polyfill'

import useIsomorphicEffect from './use-isomorphic-effect'
import { getTargetElement } from '../utils/dom'
import type { BasicTarget } from '../utils/dom'
import executeWhenChangeStop from '../utils/execute-when-change-stop'
import { noop } from '../utils/function'

type Size = { width?: number; height?: number }

function useSize(
  target?: BasicTarget,
  options?: {
    emitWhenChangeStop?: boolean | number
  }
): Size {
  const { emitWhenChangeStop } = options ?? {}
  const [state, setState] = useState<Size>(() => {
    const el = getTargetElement(target)
    return {
      width: ((el || {}) as HTMLElement).clientWidth,
      height: ((el || {}) as HTMLElement).clientHeight
    }
  })

  useIsomorphicEffect(() => {
    const el = getTargetElement(target)
    if (!el) {
      return () => {}
    }

    const onResize: ResizeObserverCallback = (
      entries: readonly ResizeObserverEntry[]
    ) => {
      const target = entries[0].target as HTMLElement

      const { width, height } = target.getBoundingClientRect()

      /**
       * Resize observer trigger when content size changed.
       * In most case we just care about element size,
       * let's use `boundary` instead of `contentRect` here to avoid shaking.
       */
      const fixedWidth = Math.floor(width)
      const fixedHeight = Math.floor(height)

      if (state.width !== fixedWidth || state.height !== fixedHeight) {
        const size = { width: fixedWidth, height: fixedHeight }

        setState(size)
      }
    }

    const [callback, reset] =
      typeof emitWhenChangeStop === 'number'
        ? executeWhenChangeStop(onResize, emitWhenChangeStop)
        : typeof emitWhenChangeStop === 'boolean'
          ? executeWhenChangeStop(onResize)
          : [onResize, noop]

    const resizeObserver = new ResizeObserver(callback)

    resizeObserver.observe(el as HTMLElement)
    return () => {
      resizeObserver.disconnect()
      reset()
    }
  }, [target, state, emitWhenChangeStop])

  return state
}

export default useSize
